// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  FinNotificationItem,
  NotificationDateGroup,
  NotificationEmptyState,
} from '@loa/ui-components';
import { useNotifications } from '../contexts/NotificationContext';
import { groupNotificationsByDate } from '../utils/notificationGrouping';
import { getNotificationRoute } from '../utils/notificationRoutes';
import type { AdvisorNotification } from '../types/notification';
import './NotificationsPage.css';

type NotificationGroup = ReturnType<typeof groupNotificationsByDate>[number];

function renderGroupItem(
  item: NotificationGroup['items'][number],
  onItemClick: (id: string) => Promise<void> | void,
  onDeleteItem: (id: string) => Promise<void> | void
) {
  const { notification, timeLabel } = item;
  return (
    <FinNotificationItem
      key={notification.id}
      notification={notification}
      timeLabel={timeLabel}
      variant="full"
      onClick={(id: string) => {
        onItemClick(id);
      }}
      onDelete={(id: string) => {
        onDeleteItem(id);
      }}
    />
  );
}

function renderNotificationList(args: {
  loading: boolean;
  notifications: AdvisorNotification[];
  groups: NotificationGroup[];
  onItemClick: (id: string) => Promise<void> | void;
  onDeleteItem: (id: string) => Promise<void> | void;
}) {
  const { loading, notifications, groups, onItemClick, onDeleteItem } = args;
  if (loading && notifications.length === 0) {
    return (
      <div className="notifications-page__loader">
        <span className="notifications-page__spinner" />
      </div>
    );
  }
  if (notifications.length === 0) {
    return <NotificationEmptyState />;
  }
  return groups.map((group) => (
    <div key={group.label}>
      <NotificationDateGroup label={group.label} />
      {group.items.map((item) => renderGroupItem(item, onItemClick, onDeleteItem))}
    </div>
  ));
}

export function NotificationsPage() {
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    loading,
    error,
    hasMore,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();

  useEffect(() => {
    fetchNotifications(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleItemClick = async (id: string) => {
    const notification = notifications.find((n) => n.id === id);
    if (!notification) return;

    if (!notification.isRead) {
      await markAsRead(notification.id);
    }

    const route = getNotificationRoute(notification);
    if (route) {
      navigate(route);
    }
  };

  const handleMarkAllRead = async () => {
    await markAllAsRead();
  };

  const handleLoadMore = () => {
    fetchNotifications(false);
  };

  const groups = useMemo(
    () => groupNotificationsByDate(notifications),
    [notifications]
  );

  const titleText = unreadCount > 0
    ? `Notifications - ${unreadCount} unread`
    : 'Notifications';

  return (
    <div className="notifications-page">
      {error && (
        <div className="notifications-page__error">{error}</div>
      )}

      <div className="notifications-page__card">
        {/* Card Header */}
        <div className="notifications-page__card-header">
          <h2 className="notifications-page__title">{titleText}</h2>
          {unreadCount > 0 && (
            <Button
              variant="filled"
              colour="purple"
              size="lg"
              onClick={handleMarkAllRead}
            >
              Mark all as read
            </Button>
          )}
        </div>

        {/* Notification List */}
        <div className="notifications-page__list">
          {renderNotificationList({
            loading,
            notifications,
            groups,
            onItemClick: handleItemClick,
            onDeleteItem: deleteNotification,
          })}
        </div>

        {/* Pagination */}
        {hasMore && notifications.length > 0 && (
          <div className="notifications-page__footer">
            <Button
              variant="outlined"
              colour="purple"
              size="sm"
              loading={loading}
              onClick={handleLoadMore}
            >
              Load more notifications
            </Button>
          </div>
        )}
      </div>

      {!hasMore && notifications.length > 0 && (
        <div className="notifications-page__end-message">
          You've reached the end of your notifications
        </div>
      )}
    </div>
  );
}
